/**
 * Hook generico per caricare Static List
 *
 * Centralizza la logica di caricamento delle liste statiche/dinamiche.
 * Usa il registry per determinare se caricare da API o usare dati fallback.
 *
 * @example
 * // Lista da API (carica subito)
 * const { data, options, isLoading } = useStaticList("tipologia_documento");
 *
 * @example
 * // Lista lazy (carica solo quando si apre dropdown)
 * const { options, isLoading, onOpen } = useLazyStaticList("soggetto_attuatore");
 * <EdSelect options={options} onOpen={onOpen} loading={isLoading} />
 *
 * @example
 * // Lista statica (no API call)
 * const { options } = useStaticList("genere");
 */

import { useState, useCallback } from "react";
import { useQuery } from "@tanstack/react-query";
import { apiClient } from "@/core/api/axios-client";
import type {
  StaticListResponse,
  StaticListItem,
  SelectOption,
} from "@/core/api/types/staticList";
import { getListConfig } from "@/core/config/staticListRegistry";

// Opzioni hook
export interface UseStaticListOptions {
  /** Abilita/disabilita la query (default: true) */
  enabled?: boolean;
  /** Usa 'value' come valore se presente, altrimenti 'id' (default: true) */
  useValueField?: boolean;
}

// Return type
export interface UseStaticListResult {
  /** Dati raw dalla lista */
  data: StaticListItem[];
  /** Opzioni pre-formattate per EdSelect */
  options: SelectOption[];
  /** Loading state */
  isLoading: boolean;
  /** Error state */
  isError: boolean;
  /** Error object */
  error: Error | null;
}

/**
 * Hook generico per caricare Static List dal registry
 *
 * @param listName - Identificativo della lista (es. "tipologia_documento", "genere")
 * @param options - Opzioni aggiuntive
 */
export function useStaticList(
  listName: string,
  options: UseStaticListOptions = {}
): UseStaticListResult {
  const { enabled = true, useValueField = true } = options;

  const config = getListConfig(listName);

  // Se non c'e' configurazione, ritorna errore
  if (!config) {
    console.warn(`[useStaticList] Lista non registrata: ${listName}`);
    return {
      data: [],
      options: [],
      isLoading: false,
      isError: true,
      error: new Error(`Lista "${listName}" non registrata nel registry`),
    };
  }

  const hasApiEndpoint = !!config.endpoint;
  const queryKey = config.queryKey || listName;

  // Query per liste da API
  const query = useQuery({
    queryKey: ["staticList", queryKey],
    queryFn: async (): Promise<StaticListItem[]> => {
      if (!config.endpoint) {
        return config.fallback || [];
      }

      const response = await apiClient.get<StaticListResponse>(config.endpoint);
      return response.data.data;
    },
    enabled: enabled && hasApiEndpoint,
    staleTime: Infinity,
  });

  // Dati: da API o da fallback
  const data: StaticListItem[] = hasApiEndpoint
    ? query.data || config.fallback || []
    : config.fallback || [];

  // Trasforma in formato EdSelect
  // Se item ha 'value', usa quello; altrimenti usa 'id'
  const selectOptions: SelectOption[] = data.map((item) => ({
    value: useValueField && item.value !== undefined ? item.value : item.id,
    label: item.name,
  }));

  return {
    data,
    options: selectOptions,
    isLoading: hasApiEndpoint ? query.isLoading : false,
    isError: hasApiEndpoint ? query.isError : false,
    error: hasApiEndpoint ? (query.error as Error | null) : null,
  };
}

// === Hook specifici per backward compatibility ===

/**
 * Hook per tipologie corsi
 * @deprecated Usa useStaticList("tipologie_corsi") invece
 */
export function useTipologieCorsi(opts?: UseStaticListOptions) {
  const result = useStaticList("tipologie_corsi", opts);
  return {
    tipologie: result.data,
    options: result.options,
    isLoading: result.isLoading,
  };
}

/**
 * Hook per categorie corsi
 * @deprecated Usa useStaticList("categorie_corsi") invece
 */
export function useCategories(opts?: UseStaticListOptions) {
  const result = useStaticList("categorie_corsi", opts);
  return {
    categories: result.data,
    options: result.options,
    isLoading: result.isLoading,
  };
}

/**
 * Hook per tipologia documento
 * @deprecated Usa useStaticList("tipologia_documento") invece
 */
export function useTipologiaDocumento(opts?: UseStaticListOptions) {
  const result = useStaticList("tipologia_documento", opts);
  return {
    tipologie: result.data,
    options: result.options,
    isLoading: result.isLoading,
  };
}

/**
 * Hook per soggetti attuatori
 * @deprecated Usa useStaticList("soggetto_attuatore") invece
 */
export function useSoggettiAttuatori(opts?: UseStaticListOptions) {
  const result = useStaticList("soggetto_attuatore", opts);
  return {
    soggettiAttuatori: result.data,
    options: result.options,
    isLoading: result.isLoading,
  };
}

/**
 * Hook per genere (M/F)
 */
export function useGenere() {
  return useStaticList("genere");
}

/**
 * Hook per tipo lezione (Teoria/Pratica)
 */
export function useTipoLezione() {
  return useStaticList("tipo_lezione");
}

// === LAZY LOADING ===

export interface UseLazyStaticListResult extends UseStaticListResult {
  /** Callback da passare a EdSelect onOpen - triggera il fetch */
  onOpen: () => void;
  /** True se è stato triggerato almeno una volta */
  hasLoaded: boolean;
}

/**
 * Hook per lazy loading delle static list.
 * Carica i dati solo quando si apre la dropdown e ricarica ad ogni apertura.
 *
 * @example
 * const { options, isLoading, onOpen } = useLazyStaticList("soggetto_attuatore");
 *
 * <EdSelect
 *   options={options}
 *   onOpen={onOpen}
 *   loading={isLoading}
 *   placeholder="Seleziona..."
 * />
 */
export function useLazyStaticList(
  listName: string,
  options: Omit<UseStaticListOptions, "enabled"> = {}
): UseLazyStaticListResult {
  const { useValueField = true } = options;
  const [hasTriggered, setHasTriggered] = useState(false);

  const config = getListConfig(listName);

  // Se non c'e' configurazione, ritorna errore
  if (!config) {
    console.warn(`[useLazyStaticList] Lista non registrata: ${listName}`);
    return {
      data: [],
      options: [],
      isLoading: false,
      isError: true,
      error: new Error(`Lista "${listName}" non registrata nel registry`),
      onOpen: () => {},
      hasLoaded: false,
    };
  }

  const hasApiEndpoint = !!config.endpoint;
  const queryKey = config.queryKey || listName;

  // Query per liste da API - lazy con refetch
  const query = useQuery({
    queryKey: ["staticList", queryKey],
    queryFn: async (): Promise<StaticListItem[]> => {
      if (!config.endpoint) {
        return config.fallback || [];
      }

      const response = await apiClient.get<StaticListResponse>(config.endpoint);
      return response.data.data;
    },
    enabled: hasTriggered && hasApiEndpoint,
    staleTime: 0, // Sempre refetch quando triggerato
  });

  // Funzione da passare a EdSelect onOpen
  const onOpen = useCallback(() => {
    if (hasApiEndpoint) {
      if (hasTriggered) {
        // Se già triggerato, refetch
        query.refetch();
      } else {
        // Prima volta, abilita la query
        setHasTriggered(true);
      }
    }
  }, [hasTriggered, hasApiEndpoint, query]);

  // Dati: da API o da fallback
  const data: StaticListItem[] = hasApiEndpoint
    ? query.data || config.fallback || []
    : config.fallback || [];

  // Trasforma in formato EdSelect
  const selectOptions: SelectOption[] = data.map((item) => ({
    value: useValueField && item.value !== undefined ? item.value : item.id,
    label: item.name,
  }));

  return {
    data,
    options: selectOptions,
    isLoading: hasApiEndpoint ? query.isLoading || query.isFetching : false,
    isError: hasApiEndpoint ? query.isError : false,
    error: hasApiEndpoint ? (query.error as Error | null) : null,
    onOpen,
    hasLoaded: hasTriggered && !query.isLoading,
  };
}
